/**
 * Shared helpers/consts for the Planner views (calendar, list, kanban, gantt).
 */
import type { PlannerTaxonomies, PlannerTaxTree } from '@/services/api';

export const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
export const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
export const SLOT_PX = 40; // calendar: px per interval slot
export const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']; // 0..6 = Mon..Sun

// Block / column colour by task-state slug.
// 'planned' uses the brand green (vida) instead of the default blue so the
// calendar / list / kanban / gantt match the look & feel of the rest of the app.
export const STATE_COLORS: Record<string, string> = {
  planned: 'bg-vida-600',
  pending: 'bg-amber-500',
  finished: 'bg-green-600',
  cancelled: 'bg-gray-400',
};
export const STATE_BORDER: Record<string, string> = {
  planned: 'border-vida-300',
  pending: 'border-amber-300',
  finished: 'border-green-300',
  cancelled: 'border-gray-300',
};

export const ALARM_OPTIONS = [
  { label: 'Sin aviso', min: -1 },
  { label: 'Al inicio', min: 0 },
  { label: '5 min antes', min: 5 },
  { label: '15 min antes', min: 15 },
  { label: '30 min antes', min: 30 },
  { label: '1 h antes', min: 60 },
];

export function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function hm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** JS getDay() (0=Sun..6=Sat) → Mon..Sun index (0=Mon..6=Sun). */
export function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}
export function startOfWeek(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  r.setDate(r.getDate() - mondayIndex(r));
  return r;
}
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
export function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}
export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
/** Bucket key for a date: 'YYYY-MM-DD' (day) or 'YYYY-MM' (month). */
export function ym(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function treeById(tax: PlannerTaxonomies | null, id: string): PlannerTaxTree | undefined {
  return tax?.trees.find((t) => t.id === id);
}
export function stateInfo(tax: PlannerTaxonomies | null, nodeId?: string | null): { id: string; name: string } | undefined {
  if (!nodeId) return undefined;
  const n = treeById(tax, 'task-states')?.nodes.find((x) => x._id === nodeId);
  return n ? { id: n.id, name: n.name } : undefined;
}
